import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ConfirmModal } from '../../../components/modal/confirm.modal';
import { formatNumber, numberToKorean } from '../../../utils/util';
import { TableComponent } from '../../../components/user/dairy/user.dairy.transaction.statement.table';
import * as XLSX from 'xlsx';
import {
  fetchTransactionBelonged,
  fetchTransactionRent,
} from '../../../api/report';

interface dataProps {
  startDate: Date;
  endDate: Date;
  selectedType: string;
  name: string;
}

// TransactionItemDto 인터페이스
interface TransactionItemDto {
  arrivedAt: string;
  dayType: string;
  amount: number;
  tax: number;
  note?: string;
}

// TransactionReportDtoRx 인터페이스
interface TransactionReportDtoRx {
  name: string; // 기업명
  today: string; // 오늘 날짜 (ISO 8601 형식)
  totalAmount: number; // 합계 금액
  transactions: TransactionItemDto[]; // TransactionItemDto 배열
}

const UserDairyTransactionStatementRegist = () => {
  const location = useLocation<dataProps>();
  const [transactionData, setTransactionData] =
    useState<TransactionReportDtoRx | null>(null); // 거래 데이터를 저장할 상태
  const [loading, setLoading] = useState(false); // 로딩 상태
  const [notes, setNotes] = useState<{ [key: string]: string }>({}); // 각 행의 비고 값을 저장할 상태

  const today = new Date();
  const formattedDate = today.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const [confirmModal, setConfirmModal] = useState(false);
  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  const handleModalOkey = () => {
    setConfirmModal(false);
    downloadExcel(); // 엑셀 다운로드 함수 호출
  };

  const downloadExcel = () => {
    if (!transactionData || !transactionData.transactions) return;

    // transactions 데이터를 엑셀로 변환, note는 사용자 입력을 참조
    const worksheet = XLSX.utils.json_to_sheet(
      transactionData.transactions.map((item, index) => ({
        날짜: item.arrivedAt,
        작업_형태: item.dayType === 'half' ? '반일' : '전일',
        금액: formatNumber(item.amount),
        세액: formatNumber(item.amount * 0.1), // amount에 0.1 곱해 세액 계산
        비고: notes[`note-${index}`] || '', // note 상태에서 가져옴
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
    XLSX.writeFile(workbook, 'transaction_data.xlsx');
  };

  const totalCostInKorean = numberToKorean(
    Number(transactionData?.totalAmount),
  );

  useEffect(() => {
    // location.state가 존재하는지 확인
    if (location.state) {
      const { startDate, endDate, name, selectedType } = location.state;

      // API 요청을 위한 파라미터 설정
      const params = {
        startDate: startDate.toISOString().split('T')[0], // 'YYYY-MM-DD' 형식으로 변환
        endDate: endDate.toISOString().split('T')[0], // 'YYYY-MM-DD' 형식으로 변환
        name: name, // 회사 이름
        reportType: selectedType, // 보고서 타입 (일대차, 마당차 등)
      };

      // 거래 데이터를 불러오는 함수
      const getDataList = async () => {
        setLoading(true); // 로딩 시작
        try {
          let response;

          // selectedType에 따라 다르게 API 호출
          if (selectedType === 'belonged') {
            response = await fetchTransactionBelonged(params); // '일대차'인 경우
          } else if (selectedType === 'rent') {
            response = await fetchTransactionRent(params); // '마당차'인 경우
          } else {
            console.error('알 수 없는 보고서 타입:', selectedType);
            return; // 타입이 일치하지 않으면 처리 중단
          }

          setTransactionData(response); // 응답 데이터를 상태에 저장
        } catch (error) {
          console.error('데이터 가져오기 오류:', error);
        } finally {
          setLoading(false); // 로딩 종료
        }
      };

      getDataList(); // 함수 호출
    }
  }, [location.state]);

  return (
    <Container>
      <Wrap>
        <div className="title">{formattedDate}</div>
        <div className="summary">
          <div className="top">
            <div className="company">
              {transactionData?.name} <span>귀하</span>
            </div>
            <div className="info-txt">
              <span>합계금액(공급가+세액)</span>
            </div>
          </div>
          <div className="bt">
            <div className="total-cost-txt">
              {totalCostInKorean}
              <span>원</span>(
              {formatNumber(Number(transactionData?.totalAmount))})
            </div>
          </div>
        </div>
        <TableComponent
          data={transactionData?.transactions || []} // transactions 배열 전달
          setConfirmModal={setConfirmModal}
          onUpdateNotes={setNotes} // notes 상태 업데이트 함수 전달
        />
      </Wrap>
      {/* <ConfirmBtn onClick={handleConfirmClick}>다운로드</ConfirmBtn> */}
      <AnimatePresence initial={false}>
        {confirmModal ? (
          <ConfirmModal
            handleConfirmClose={handleConfirmClose}
            confirmText="다운로드 하시겠습니까?"
            onClick={handleModalOkey}
          />
        ) : null}
      </AnimatePresence>
    </Container>
  );
};

export default UserDairyTransactionStatementRegist;

// style
const Container = styled.div`
  padding: 0 1.5rem;
`;

const Wrap = styled.div`
  .title {
    margin-top: 2.4rem;
    font-weight: 400;
    font-size: 1.2rem;
    color: ${(props) => props.theme.gray900};
  }
  .summary {
    margin-top: 1.2rem;
    .top {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      .company {
        font-size: 1.6rem;
        font-weight: 700;
        color: ${(props) => props.theme.textBlackColor};
        line-height: 2rem;
        margin: 0.5rem;
        span {
          font-size: 1.2rem;
          font-weight: 400;
        }
      }
      .info-txt {
        font-size: 1.2rem;
        font-weight: 500;
        color: ${(props) => props.theme.gray900};
      }
    }
    .bt {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.8rem;
      .total-cost-txt {
        font-size: 1.6rem;
        font-weight: 600;
      }
    }
  }
`;

const ConfirmBtn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;
